import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Space } from "antd";
import TableRowActionButtons from "components/TableRowActionButtons";
import { useModernQueryWithPaginationAndOrder } from "hooks";
import PropTypes from "prop-types";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { tablesKey } from "../../../../../api/queries/tables";
import { Button, Form, Layout, Modal, Table } from "../../../../../components/styleguide";
import { AdminTypes } from "../../../../../enums/common";
import TableType from "../../../../../enums/TableType";
import VenueContext from "../../../../../pages/venueContext";
import BulkAddTables from "./BulkAddTables";
import EditTable from "./EditTable";

import styles from "./styles.module.less";

const { Header, Content } = Layout;
const { confirm } = Modal;

export default function Tables({ ui, facility, onBulkFinish }) {
  const { t } = useTranslation();
  const [isAddTableVisible, setIsTableVisible] = useState(false);
  const [isBulkAddTableVisible, setIsBulkAddTableVisible] = useState(false);
  const [tables, setTables] = useState([]);
  const [tableTypeFilter, setTableTypeFilter] = useState();
  const [activeTable, setActiveTable] = useState(null);
  const [form] = Form.useForm();
  const showBulkAddTables = () => {
    form.resetFields();
    setIsBulkAddTableVisible(true);
  };

  const hideBulkAddTables = () => {
    setActiveTable(null);
    setIsBulkAddTableVisible(false);
  };

  const onBulkAddTablesFinish = () => {
    useTablesPaginatedQuery.invalidateQuery();
    hideBulkAddTables();
    onBulkFinish?.();
  };

  const showAddTable = () => {
    form.resetFields();
    setIsTableVisible(true);
  };
  const hideAddTable = () => {
    setActiveTable(null);
    setIsTableVisible(false);
  };

  const queryClient = useQueryClient();

  const venueActions = useContext(VenueContext);

  const { useTablesPaginatedQuery, deleteTableMutate } = venueActions;

  const {
    response: tablesQuery,
    pagination,
    handleSort,
  } = useModernQueryWithPaginationAndOrder(
    useTablesPaginatedQuery.query,
    {
      onSuccess: ({ data }) => {
        setTables(data.items);
      },
      enabled: !!facility?.id,
    },
    {
      facilityId: facility?.id,
      typeOfTable: tableTypeFilter,
    },
    { defaultPageSize: 10, pageSizeOptions: [10, 20] },
  );

  const deleteTable = (tableId) => {
    confirm({
      title: t("confirmation"),
      content: t("Are you sure you want to delete this table?"),
      icon: <ExclamationCircleOutlined />,
      okText: t("yes"),
      okType: "danger",
      confirmLoading: deleteTableMutate.isLoading,
      cancelText: t("no"),
      onOk: async () => {
        await deleteTableMutate(
          { tableId },
          {
            onSuccess: () => {
              queryClient.invalidateQueries(tablesKey);
            },
          },
        );
      },
    });
  };

  const editTable = (table) => {
    form.resetFields();
    setActiveTable(table);
    showAddTable();
  };

  const columns = [
    {
      title: t("Table Number"),
      dataIndex: "tableNumber",
      sorter: true,
    },
    {
      title: t("Table Type"),
      dataIndex: "tableType",
      render: (tableType) => TableType.toDisplayName(tableType),
      filters: Object.values(TableType)
        .filter((value) => typeof value === "number")
        .map((value) => ({
          text: TableType.toDisplayName(value),
          value,
        })),
      filterMultiple: false,
    },
    {
      title: t("Number of Seats"),
      dataIndex: "numberOfSeats",
    },
    {
      title: "",
      dataIndex: "id",
      render: (id, record) => (
        <TableRowActionButtons onEdit={() => editTable(record)} onDelete={() => deleteTable(id)} />
      ),
    },
  ];

  const handleChange = (_, filters, sorter) => {
    setTableTypeFilter(filters?.tableType?.[0] ?? undefined);
    handleSort(_, _, sorter);
  };

  return (
    <div>
      <Layout>
        <Header className={styles.header}>
          <Space>
            <Button onClick={showAddTable}>{t("Add Table")}</Button>
            <Button onClick={showBulkAddTables}>{t("Bulk Add Tables")}</Button>
          </Space>
        </Header>
        <Content id="tablesContent">
          <EditTable
            ui={ui}
            visible={isAddTableVisible}
            onClose={hideAddTable}
            facility={facility}
            activeTable={activeTable}
            tablesLoading={tablesQuery.isLoading}
            form={form}
          />
          <BulkAddTables
            ui={ui}
            visible={isBulkAddTableVisible}
            onClose={hideBulkAddTables}
            onFinish={onBulkAddTablesFinish}
            facility={facility}
            tablesLoading={tablesQuery.isLoading}
            form={form}
          />
          <Table
            loading={tablesQuery.isLoading || deleteTableMutate.isLoading}
            columns={columns}
            onChange={handleChange}
            dataSource={tables}
            pagination={pagination}
            rowKey="id"
          />
        </Content>
      </Layout>
    </div>
  );
}

Tables.propTypes = {
  ui: PropTypes.oneOf(AdminTypes),
  facility: PropTypes.any,
  onBulkFinish: PropTypes.func,
  deleteTableMutate: PropTypes.func,
};
